<template>
	<div class="CustomerTree">
		<div :class="['CustomerTree-Item', `CustomerTree-Item--level-${localLevel}`]" v-for="item in items" :key="item.id">
			<div :class="['CustomerTree-ItemRow', {'CustomerTree-ItemRow--active': getIsActive(item.id), 'CustomerTree-ItemRow--disabled': disabledIds.includes(item.id)}]">
				<div v-if="item.children.length !== 0" class="CustomerTree-Icon" @click="toggleOpen(item.id)">
					<i :class="localOpenItems.includes(item.id) ? 'fas fa-minus' : 'fas fa-plus'"/>
				</div>
				<div v-else class="CustomerTree-Icon CustomerTree-Icon--noChildren"/>
				<div class="CustomerTree-Label" @click="setActive(item)">
					{{ $utils.format.truncateString(item.name, 30) }}
				</div>
				<ui-menu v-if="getIsActive(item.id) && !isMove && !readonly">
					<template #activator="{on, attrs}">
						<div  class="CustomerTree-Icon" v-bind="attrs" v-on="on">
							<i class="fas fa-ellipsis-v" style="color: white"/>
						</div>
					</template>
					<div class="CustomerTree-MenuItems">
						<div v-for="menuItem in getMenuItems" :key="menuItem.label" @click="menuItem.action" class="CustomerTree-MenuItem">
							<span class="icon">
								<i :class="`fas ${menuItem.icon}`"></i>
							</span>
							<span>{{ menuItem.label }}</span>
						</div>
					</div>
				</ui-menu>
			</div>
			<CustomerTree v-if="localOpenItems.includes(item.id) && item.children.length !== 0"
				:items="item.children"
				:level="localLevel"
				:open-items="localOpenItems"
				:is-move="isMove"
				:selected-id="selectedId"
				:disabled-ids="disabledIds"
				:readonly="readonly"
				@new-level="onNewLevel"
				@edit-level="onEditLevel"
				@move-level="onMoveLevel"
				@remove-level="onRemoveLevel"
				@on-select="onSelected"
			/>
		</div>
		<div v-if="!items.length && isMove" class="CustomerTree-Item CustomerTree-Item--level-1">
			<div class="CustomerTree-ItemRow">
				<div class="CustomerTree-Icon CustomerTree-Icon--noChildren"></div>
				<div class="CustomerTree-Label no-point">{{ $t('customer_tree.no_results') }}</div>
			</div>
		</div>
	</div>
</template>

<script>
import CustomerTree from '@/components/customer/customer-tree';
import { mapGetters, mapActions } from 'vuex';
export default {
	name: 'CustomerTree',

	components: {
		CustomerTree
	},

	props: {
		items: {
			type: Array,
			default: () => ([])
		},

		level: {
			type: Number,
			default: 0
		},

		openItems: {
			type: Array,
			default: () => ([])
		},

		isMove: {
			type: Boolean,
			default: false
		},

		selectedId: {
			type: String,
			default: null
		},

		disabledIds: {
			type: Array,
			default: () => ([])
		},

		readonly: {
			type: Boolean,
			default: false
		}
	},

	data: () => ({
		localLevel: 0,
		localOpenItems: []
	}),

	computed: {
		...mapGetters('user', ['getActiveCustomer']),

		getMenuItems() {
			return [{
				label: this.$t('customer_tree.new_level'),
				icon: 'fa-plus',
				action: this.onNewLevel
			}, {
				label: this.$t('customer_tree.edit_level'),
				icon: 'fa-pen',
				action: this.onEditLevel
			}, this.$isAllowedTo('tree_move_level') && {
				label: this.$t('customer_tree.move_level'),
				icon: 'fa-reply-all',
				action: this.onMoveLevel
			}, {
				label: this.$t('customer_tree.delete_level'),
				icon: 'fa-trash',
				action: this.onRemoveLevel
			}].filter(Boolean);
		}
	},

	watch: {
		openItems: {
			deep: true,
			handler(to) {
				this.localOpenItems = [...to];
			}
		}
	},

	created() {
		this.localLevel = this.level + 1;

		this.localOpenItems = [...this.openItems];
	},

	methods: {
		...mapActions('user', ['setActiveCustomer']),

		onNewLevel() {
			this.$emit('new-level');
		},

		onEditLevel() {
			this.$emit('edit-level');
		},

		onMoveLevel() {
			this.$emit('move-level');
		},

		onRemoveLevel() {
			this.$emit('remove-level');
		},

		onSelected(item) {
			this.$emit('on-select', item);
		},

		setActive(item) {
			if (this.disabledIds.includes(item.id)) {
				return;
			}

			if (this.isMove) {
				if (this.selectedId === item.id) {
					this.onSelected(null);

					return;
				}

				this.onSelected({...item});

				return;
			}

			this.setActiveCustomer(item);
		},

		getIsActive(customerId) {
			if (this.isMove) {
				return this.selectedId === customerId;
			}

			return this.getActiveCustomer?.id === customerId;
		},

		toggleOpen(id) {
			if (this.localOpenItems.includes(id)) {
				this.localOpenItems = this.localOpenItems.filter(openId => openId !== id);

				return;
			}

			this.localOpenItems = [...this.localOpenItems, id];
		}
	}
};
</script>

<style lang="scss">
.CustomerTree__container > .CustomerTree {
	overflow-y: scroll;
	margin-top: 1rem;
	padding-right: .5rem;

	&::-webkit-scrollbar {
		width: 10px;
		height: 10px;
		padding-left: 5px;
	}

	&::-webkit-scrollbar-track {
		display: none;
	}

	&::-webkit-scrollbar-thumb {
		background: orange;
		border-radius: 10px;
	}
}

.CustomerTree-MenuItems {
	width: 150px;
}



.CustomerTree-MenuItem {
	cursor: pointer;
	font-size: 12px;
	background-color: white;
	border-radius: 3px;
	margin-bottom: 2px;
	transition: background-color 0.3s ease;


	&:hover {
		transition: background-color 0.3s ease;
		background-color: darken(white, 10%);
	}
}

.CustomerTree-MenuItem:last-child {
	margin-bottom: 0px;
}

.CustomerTree-Item--level-1 {
	padding: 5px;
	background-color: lighten(#363a41, 10%);
	margin-top: 10px;
	border-radius: 4px;

	&:first-child {
		margin-top: 0;
	}
}

.CustomerTree-Icon {
	cursor: pointer;
	display: flex;
	justify-content: center;
	align-items: center;
	height: 100%;
	width: 30px;
	background-color: lighten(#363a41, 10%);;
	transition: background-color 0.3s ease;
	border-radius: 4px;


	&:hover {
		transition: background-color 0.3s ease;
		background-color: lighten(#363a41, 20%);;
	}
}

.CustomerTree-Icon--noChildren {
	pointer-events: none;
}

.CustomerTree-Label {
	cursor: pointer;
	font-size: 12px;
	display: flex;
	align-items: center;
	height: 100%;
	width: calc(100% - 60px);
	color: white;
	padding-left: 5px;
	background-color: lighten(#363a41, 10%);;
	transition: background-color 0.3s ease;
	border-radius: 4px;

	&:hover:not(.no-point) {
		transition: background-color 0.3s ease;
		background-color: lighten(#363a41, 20%);;
	}
	
	&.no-point {
		cursor: default;
	}
}

.CustomerTree-Item:not(.CustomerTree-Item--level-1) {
	padding-left: 20px;
}

.CustomerTree-ItemRow {
	overflow: hidden;
	display: flex;
	align-items: center;
	height: 30px;
}

.CustomerTree-ItemRow--active > .CustomerTree-Label {
	color: #ef7c21;
	font-weight: bold;
}

.CustomerTree-ItemRow--disabled {
	opacity: 0.3;
}
</style>
